<section class="translations" aria-labelledby="translationsTitle">
    <div class="translations__header">
        <div>
            <h1 id="translationsTitle">Your Code Translations</h1>
            <p>Here you can find all your code translations, along with their status and details. To download a
                completed translation click the download icon next to the translated file name.</p>
            <p>{{ translations().length }} {{ translations().length === 1 ? 'translation' : 'translations' }} submitted.
            </p>
        </div>

        <button class="translations__refresh secondaryBtn" type="button" [disabled]="isLoading()"
            (click)="loadTranslations()">
            Refresh
        </button>
    </div>

    @if (downloadErrorMessage()) {
    <p class="translationsDownloadError" role="alert">{{ downloadErrorMessage() }}</p>
    }

    @if (deleteErrorMessage()) {
    <p class="translationsDeleteError" role="alert">{{ deleteErrorMessage() }}</p>
    }

    @if (isLoading()) {
    <div class="translationsState" role="status">
        <p class="translationsState__title">Loading translations</p>
    </div>
    } @else if (errorMessage()) {
    <div class="translationsState translationsState--error" role="alert">
        <p class="translationsState__title">{{ errorMessage() }}</p>
        <button class="translationsState__button secondaryBtn" type="button" (click)="loadTranslations()">
            Try again
        </button>
    </div>
    } @else if (translations().length === 0) {
    <div class="translationsState">
        <p class="translationsState__title">No translations found</p>
    </div>
    } @else {
    <div class="translationsGrid" role="table" aria-label="Translations">
        <div class="translationsGrid__header" role="row">
            <span role="columnheader">Friendly name</span>
            <span role="columnheader">Original file</span>
            <span role="columnheader">Sample data files</span>
            <span role="columnheader">Expected output</span>
            <span role="columnheader">Translated file</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Actions</span>
        </div>

        @for (translation of translations(); track trackTranslation($index, translation)) {
        @let sampleDataFileNames = getSampleDataFileNames(translation);
        @let status = getStatus(translation);

        <div class="translationRow" role="row">
            <div class="translationCell translationCell--strong" role="cell" data-label="Friendly name">
                <span [title]="getFriendlyName(translation)">{{ getFriendlyName(translation) }}</span>
            </div>
            <div class="translationCell" role="cell" data-label="Original file">
                <span [title]="getOriginalFileName(translation)">{{ getOriginalFileName(translation) }}</span>
            </div>
            <div class="translationCell" role="cell" data-label="Sample data files">
                @if (sampleDataFileNames.length > 0) {
                <ul class="sampleFiles" aria-label="Sample data files">
                    @for (fileName of sampleDataFileNames; track fileName) {
                    <li [title]="fileName">{{ fileName }}</li>
                    }
                </ul>
                } @else {
                <span class="translationCell__muted">Not available</span>
                }
            </div>
            <div class="translationCell" role="cell" data-label="Expected output">
                <span [title]="getExpectedOutputFileName(translation)">
                    {{ getExpectedOutputFileName(translation) }}
                </span>
            </div>
            <div class="translationCell" role="cell" data-label="Translated file">
                <span class="translatedFile">
                    <span class="translatedFile__name" [title]="getTranslatedFileName(translation)">
                        {{ getTranslatedFileName(translation) }}
                    </span>
                    @if (canDownloadTranslation(translation)) {
                    <button class="translatedFile__download" type="button"
                        [disabled]="isDownloadingTranslation(translation)"
                        [attr.aria-label]="getDownloadButtonLabel(translation)"
                        [title]="getDownloadButtonLabel(translation)" (click)="downloadTranslation(translation)">
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M12 3v11"></path>
                            <path d="m7 9 5 5 5-5"></path>
                            <path d="M5 21h14"></path>
                        </svg>
                    </button>
                    }
                </span>
            </div>
            <div class="translationCell" role="cell" data-label="Status">
                <span class="statusPill" [class.is-complete]="status.className === 'is-complete'"
                    [class.is-failed]="status.className === 'is-failed'"
                    [class.is-queued]="status.className === 'is-queued'">
                    {{ status.label }}
                </span>
            </div>
            <div class="translationCell translationCell--actions" role="cell" data-label="Actions">
                <button class="translationDeleteButton" type="button" [disabled]="!canDeleteTranslation(translation)
                    || isDeletingTranslation(translation)" [attr.aria-label]="getDeleteButtonLabel(translation)"
                    [title]="getDeleteButtonLabel(translation)" (click)="openDeleteDialog(translation)">
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M3 6h18"></path>
                        <path d="M8 6V4h8v2"></path>
                        <path d="M6 6l1 15h10l1-15"></path>
                        <path d="M10 11v6"></path>
                        <path d="M14 11v6"></path>
                    </svg>
                </button>
            </div>
        </div>
        }
    </div>
    }

    @if (translationPendingDelete()) {
    <div class="deleteDialogBackdrop" role="presentation" (click)="closeDeleteDialog()">
        <section class="deleteDialog" role="dialog" aria-modal="true" aria-labelledby="deleteDialogTitle"
            aria-describedby="deleteDialogMessage" (click)="$event.stopPropagation()">
            <h2 id="deleteDialogTitle">Delete translation</h2>
            <p id="deleteDialogMessage">
                Are you sure you want to delete
                <strong>{{ getDeleteConfirmationName() }}</strong>?
            </p>
            <p class="deleteDialog__warning">This action cannot be undone.</p>

            <div class="deleteDialog__actions">
                <button class="secondaryBtn deleteDialog__button" type="button"
                    [disabled]="isDeletingTranslation(translationPendingDelete()!)" (click)="closeDeleteDialog()">
                    Cancel
                </button>
                <button class="deleteDialog__button deleteDialog__button--danger" type="button"
                    [disabled]="isDeletingTranslation(translationPendingDelete()!)" (click)="confirmDeleteTranslation()">
                    {{ isDeletingTranslation(translationPendingDelete()!) ? 'Deleting...' : 'Delete' }}
                </button>
            </div>
        </section>
    </div>
    }
</section>
